import React, { useRef, useState } from "react";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSIONS } from "../../constants/permissions";

export default function WorkflowToolbar({
  workflowId,
  name,
  setName,
  status,
  version,
  unsaved,
  saving,
  autoSavedAt,
  onSave,
  onSaveAs,
  onBack,
  onStatusAction,
  onAdd,
  onExport,
  onImportFile,
  onClear,
  onNameFocus,
  onNameBlur,
  canSave,
  canSaveAs,
  canChangeStatus,
}) {
  const fileRef = useRef(null);
  const [showMore, setShowMore] = useState(false);
  const canExport = usePermission(PERMISSIONS.WORKFLOW_EXPORT);
  const canImport = usePermission(PERMISSIONS.WORKFLOW_IMPORT);

  const statusOptions =
    status === "DRAFT"
      ? [{ label: "Activate", value: "ACTIVE" }]
      : status === "ACTIVE"
        ? [{ label: "Archive", value: "ARCHIVED" }]
        : status === "ARCHIVED"
          ? [{ label: "Restore to Active", value: "ACTIVE" }]
          : [];

  return (
    <div
      style={{
        height: 64,
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        borderBottom: "1px solid #e2e8f0",
        gap: 12,
        background: "#ffffff",
        position: "relative",
        zIndex: 3000,
      }}
    >
      {/* A plain <Link> can't intercept navigation for the unsaved-changes
          confirm, so this stays a button — styled as .back-link to match the
          rest of the app's back navigation. */}
      <button type="button" onClick={onBack} className="back-link">
        ← Back to Workflows
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={onNameFocus}
          onBlur={onNameBlur}
          style={{
            fontWeight: 700,
            fontSize: 18,
            border: "1px solid #dbe4f0",
            padding: "6px 8px",
            borderRadius: 8,
            minWidth: 280,
          }}
        />
        {workflowId && canChangeStatus && (
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              if (next !== status) onStatusAction(next);
            }}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #dbe4f0",
            }}
          >
            <option value={status}>{status}</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        <div style={{ fontWeight: 700, color: "#64748b", fontSize: 13 }}>
          v{version || 1}
        </div>
        {unsaved && (
          <span
            style={{
              color: "#9a3412",
              background: "#ffedd5",
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Unsaved
          </span>
        )}
        {!unsaved && autoSavedAt && (
          <span
            style={{
              color: "#166534",
              background: "#dcfce7",
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Auto-saved
          </span>
        )}
      </div>

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <button type="button" className="btn-secondary" onClick={onAdd}>
          + Add Stage
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onSave}
          disabled={saving || !canSave}
        >
          {saving ? "Saving..." : "Save"}
        </button>
        {/* <button
          type="button"
          className="btn-secondary"
          onClick={onSaveAs}
          disabled={saving || !canSaveAs}
        >
          Save As
        </button> */}

        <div style={{ position: "relative" }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowMore((v) => !v)}
          >
            ⋮
          </button>
          {showMore && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: 36,
                minWidth: 150,
                border: "1px solid #dbe4f0",
                borderRadius: 10,
                background: "#fff",
                boxShadow: "0 12px 28px rgba(15,23,42,0.14)",
                overflow: "hidden",
                zIndex: 3100,
              }}
            >
              {canExport && (
                <button
                  type="button"
                  zIndex={1}
                  onClick={() => {
                    setShowMore(false);
                    onExport();
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "#fff",
                    textAlign: "left",
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  Export JSON
                </button>
              )}
              {canImport && (
                <button
                  type="button"
                  zIndex={1}
                  onClick={() => {
                    setShowMore(false);
                    fileRef.current?.click();
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "#fff",
                    textAlign: "left",
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  Import JSON
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowMore(false);
                  onClear();
                }}
                style={{
                  width: "100%",
                  border: "none",
                  background: "#fff",
                  textAlign: "left",
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                Clear Canvas
              </button>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              files.forEach((file) => onImportFile(file));
              e.target.value = "";
            }}
          />
        </div>
      </div>
    </div>
  );
}
